import { useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { BDC_INVOICE_DEFAULT_COPIES, getBdcInvoicePdf } from "../../../services";
import { CompanyProfile } from "../../../types/company-profile.type";
import { BdcInvoiceSummary, BdcPriceCheck, BdcShipmentDetail } from "../../../types/bdc.type";
import { baht, thaiDate, thaiDateTime } from "../format";
import { CARD_SX } from "../styles";
import InvoiceA4, { INVOICE_SX } from "../Shipment/InvoiceA4";
import InvoiceCopiesField from "./InvoiceCopiesField";
import InvoiceStatusChip from "./InvoiceStatusChip";
import { openPdfResponse } from "./pdf";

/** W12 — PO prices that differ from the BDC master price (a warning; the INV uses the PO price). */
export function PriceWarnings({ check }: Readonly<{ check: BdcPriceCheck | null }>) {
  if (!check || check.warning_count === 0) return null;
  return (
    <Alert severity="warning" data-testid="bdc-price-warnings">
      <AlertTitle>ราคาใน PO ไม่ตรงกับราคาในระบบ {check.warning_count} รายการ</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {check.warnings.map((w) => (
          <li key={w.barcode}>
            {w.item_code} {w.name}: PO {baht(w.po_unit_price)} · ระบบ {w.master_price === null ? "ไม่มีราคา" : baht(w.master_price)}
            {w.difference !== null && ` (ต่าง ${w.difference > 0 ? "+" : ""}${baht(w.difference)})`}
          </li>
        ))}
      </Box>
      <Typography variant="caption">INV ใช้ราคาตาม PO ของ 7-11 · ตรวจราคาก่อนออก INV</Typography>
    </Alert>
  );
}

/**
 * แท็บ INV — the PO's INVs (live and cancelled) with the real PDF (DocGen: ทดสอบ watermark for TB…,
 * ยกเลิก stamp when cancelled). Before the first INV the draft A4 is shown, marked as not a tax
 * invoice.
 */
export default function InvoiceTab({
  detail,
  invoices,
  priceCheck,
  seller,
  canCancel,
  onCancel,
}: Readonly<{
  detail: BdcShipmentDetail;
  invoices: BdcInvoiceSummary[];
  priceCheck: BdcPriceCheck | null;
  seller: CompanyProfile | null;
  /** The PO is ออก INV แล้ว and the viewer may cancel. */
  canCancel: boolean;
  onCancel: (invoiceNo: string) => void;
}>) {
  const [busy, setBusy] = useState<string | null>(null);
  const [copies, setCopies] = useState(BDC_INVOICE_DEFAULT_COPIES);

  const openPdf = async (invoiceNo: string) => {
    setBusy(invoiceNo);
    try {
      openPdfResponse(await getBdcInvoicePdf(invoiceNo, copies), `${invoiceNo}.pdf`);
    } catch {
      // the interceptor reports it (errorMsgByStatus)
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack gap={2}>
      <PriceWarnings check={priceCheck} />
      {invoices.length > 0 && (
        <Card sx={CARD_SX}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E3E5EA" }}
          >
            <Typography fontWeight={600}>INV ของ PO นี้</Typography>
            <InvoiceCopiesField value={copies} onChange={setCopies} />
          </Stack>
          <Box sx={{ overflowX: "auto" }}>
            <table className="bdc-t" data-testid="bdc-po-invoices">
              <thead>
                <tr>
                  <th>เลข INV</th>
                  <th>วันที่</th>
                  <th>ครบกำหนด</th>
                  <th className="n">มูลค่า</th>
                  <th className="n">VAT</th>
                  <th className="n">รวม</th>
                  <th>สถานะ</th>
                  <th>ออกโดย</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_uuid}>
                    <td className="mono">
                      <b>{inv.invoice_no}</b>
                    </td>
                    <td>{thaiDate(inv.invoice_date)}</td>
                    <td>{thaiDate(inv.due_date)}</td>
                    <td className="n">{baht(inv.subtotal)}</td>
                    <td className="n">{baht(inv.vat_amount)}</td>
                    <td className="n">
                      <b>{baht(inv.grand_total)}</b>
                    </td>
                    <td>
                      <InvoiceStatusChip invoice={inv} />
                      {inv.status === "cancelled" && (
                        <div className="sub">
                          {inv.cancel_reason} · {inv.cancelled_by_name ?? ""} {thaiDateTime(inv.cancelled_at)}
                        </div>
                      )}
                    </td>
                    <td className="sub">
                      {inv.created_by_name ?? ""} {thaiDateTime(inv.created_at)}
                    </td>
                    <td>
                      <Stack direction="row" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PictureAsPdfIcon />}
                          loading={busy === inv.invoice_no}
                          onClick={() => openPdf(inv.invoice_no)}
                        >
                          PDF
                        </Button>
                        {canCancel && inv.status === "issued" && (
                          <Button size="small" color="error" variant="outlined" onClick={() => onCancel(inv.invoice_no)}>
                            ยกเลิก INV
                          </Button>
                        )}
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Card>
      )}
      {!invoices.some((inv) => inv.status === "issued") && (
        <>
          <Alert severity="info">
            {invoices.length === 0 ? "PO นี้ยังไม่ออก INV" : "INV ของ PO นี้ถูกยกเลิกแล้ว"} — ด้านล่างเป็นร่างจากยอด PO
            (ไม่ใช่ใบกำกับภาษี) · INV ต้นฉบับจะออกได้หลังยืนยัน DO
          </Alert>
          <Box sx={{ bgcolor: "#EDEEF1", p: 2, borderRadius: "12px", overflowX: "auto", ...INVOICE_SX }}>
            <InvoiceA4 detail={detail} seller={seller} />
          </Box>
        </>
      )}
    </Stack>
  );
}
